import { Create, useForm, useSelect } from "@refinedev/antd";
import { HttpError, useGo } from "@refinedev/core";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Row,
  Col,
  Card,
  App,
  Upload,
  Breadcrumb,
  Space,
} from "antd";
import {
  InboxOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useLocation, Link } from "react-router";
import L from "leaflet";
import "leaflet-draw"; // attaches L.GeometryUtil used for area calc
import type { AdjacentCropFromAuditFormValues } from "../../types/adjacent-crop";
import { adjacentCropService, auditPlotService } from "../../services/api";
import { AdjacentCropMapEditor } from "./components/AdjacentCropMapEditor";
import { AdjacentCropVarietyPicker } from "./components/AdjacentCropVarietyPicker";
import "./adjacent-crops.css";
import { usePageTitle } from "../../hooks";

/** Geodesic area in hectares for a GeoJSON Polygon or MultiPolygon.
 *  Uses `L.GeometryUtil.geodesicArea` (from leaflet-draw), which is
 *  accurate enough for an "approximate surface" hint shown next to
 *  the user-editable estimated_surface input. Returns null for
 *  Points or unsupported shapes. */
const polygonAreaHa = (geom: GeoJSON.Geometry | null): number | null => {
  if (!geom) return null;
  const util = (L as any).GeometryUtil;
  if (!util?.geodesicArea) return null;
  const ringToLatLngs = (ring: number[][]) =>
    ring.map(([lng, lat]) => L.latLng(lat, lng));
  try {
    if (geom.type === "Polygon") {
      const ring = (geom as GeoJSON.Polygon).coordinates[0];
      const m2 = util.geodesicArea(ringToLatLngs(ring));
      return m2 / 10000;
    }
    if (geom.type === "MultiPolygon") {
      const total = (geom as GeoJSON.MultiPolygon).coordinates.reduce(
        (acc, poly) => acc + util.geodesicArea(ringToLatLngs(poly[0])),
        0,
      );
      return total / 10000;
    }
  } catch {
    return null;
  }
  return null;
};

const { TextArea } = Input;
const { Dragger } = Upload;

interface PhotoItem {
  uid: string;
  file: File;
  description: string;
  preview: string;
}

/**
 * AdjacentCropCreate Component
 * Form for creating a new adjacent crop from an audit context
 * Automatically associates with audit's farm and creates first review
 */
export const AdjacentCropCreate = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const go = useGo();
  const [searchParams] = useSearchParams();
  const auditId = searchParams.get("auditId");
  const location = useLocation();
  // Geometry pre-loaded from the audit-detail map's draw flow.
  const initialGeometryFromRoute = (location.state as any)
    ?.initialGeometry as GeoJSON.Geometry | undefined;

  // Set page title
  usePageTitle(t("adjacent_crops.create.title", "Crear Cultivo Adyacente"));

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Start `true` so the species `useSelect` waits for audit data to
  // load — otherwise the species query fires on mount, then gets
  // cancelled when `loadAuditData()` flips this to `true`, and
  // react-query may not refetch on the toggle back to `false`.
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [geometry, setGeometry] = useState<GeoJSON.Geometry | null>(
    initialGeometryFromRoute ?? null,
  );
  const [referencePlots, setReferencePlots] = useState<GeoJSON.FeatureCollection | null>(null);
  const [controlPoints, setControlPoints] = useState<GeoJSON.FeatureCollection | null>(null);
  const [farmId, setFarmId] = useState<number | null>(null);
  const [auditReference, setAuditReference] = useState<string>("");
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  // Surface (ha) derived from the drawn polygon — shown as a hint
  // under the estimated_surface input so the auditor has a sanity
  // reference without forcing it as the saved value.
  const geometryAreaHa = useMemo(() => polygonAreaHa(geometry), [geometry]);

  const { formProps, form } = useForm<
    AdjacentCropFromAuditFormValues,
    HttpError,
    AdjacentCropFromAuditFormValues
  >({
    action: "create",
    redirect: false,
    warnWhenUnsavedChanges: false,
  });

  // Load species for select dropdown (after map data is loaded)
  const { selectProps: speciesSelectProps } = useSelect({
    resource: "crop-species",
    optionLabel: "name",
    optionValue: "id",
    filters: [
      {
        field: "is_active",
        operator: "eq",
        value: true,
      },
    ],
    pagination: {
      pageSize: 500, // Request 500 items per page
    },
    sorters: [
      {
        field: "name",
        order: "asc",
      },
    ],
    queryOptions: {
      enabled: !isLoadingData, // Only load after map data is ready
    },
  });

  // El listado precargado de variedades ya no vive aquí: el nuevo
  // `AdjacentCropVarietyPicker` hace su propio search remoto sobre
  // `/crop-varieties/` a partir de lo que teclea el auditor y se
  // encarga de la creación inline. `species` se rellena en el form
  // desde el `onChange` del picker.

  // Validate that we have an audit ID and load farm data
  useEffect(() => {
    if (!auditId) {
      message.error(
        t(
          "adjacent_crops.error.noAuditId",
          "No se especificó una auditoría. Por favor, cree el cultivo adyacente desde el panel de auditoría."
        )
      );
      go({ to: "/audits" });
      return;
    }

    let isMounted = true;

    // Load audit data to get farm ID and reference plots
    const loadAuditData = async () => {
      setIsLoadingData(true);
      try {
        // Fetch audit data using apiClient
        const { apiClient } = await import('../../services/api/apiClient');
        const auditResponse = await apiClient.get(`/audits/${auditId}/`);
        const audit = auditResponse.data;


        if (!isMounted) return;

        if (!audit.farm) {
          message.error(t("adjacent_crops.error.noFarm", "La auditoría no tiene una finca asociada"));
          go({ to: `/audits/${auditId}` });
          return;
        }

        // Extract farm ID - it could be a number or an object with id
        const farmIdValue = typeof audit.farm === 'object' ? audit.farm.id : audit.farm;
        setFarmId(farmIdValue);
        setAuditReference(audit.reference);

        // Load plots for this audit
        try {
          const plotsResponse = await auditPlotService.getPlotsByAudit(Number(auditId));
          
          if (!isMounted) return;
          
          // Handle response format: { count, results: { type: "FeatureCollection", features: [...] } }
          let featureCollection: GeoJSON.FeatureCollection | null = null;
          
          if (plotsResponse && typeof plotsResponse === 'object' && 'results' in plotsResponse) {
            const results = (plotsResponse as any).results;
            
            // Check if results is a FeatureCollection
            if (results && typeof results === 'object' && results.type === 'FeatureCollection' && 'features' in results) {
              // Filter out features without geometry and map to proper format
              const validFeatures = (results.features as any[])
                .filter((feature: any) => feature.geometry !== null)
                .map((feature: any) => ({
                  type: "Feature" as const,
                  geometry: feature.geometry,
                  properties: {
                    name: feature.properties?.name || `Parcela ${feature.id}`,
                    surface: feature.properties?.surface,
                  },
                }));
              
              if (validFeatures.length > 0) {
                featureCollection = {
                  type: "FeatureCollection",
                  features: validFeatures,
                };
              }
            }
          }
          
          if (featureCollection) {
            setReferencePlots(featureCollection);
          }
        } catch {
          // Reference plots are best-effort; map still works without them.
        }

        // Load control points for this farm
        try {
          const cpResponse = await apiClient.get(
            `/audit-control-points/open-for-farm/?farm_id=${farmIdValue}&audit_id=${auditId}`
          );
          
          if (!isMounted) return;
          
          const controlPointsData = cpResponse.data.control_points;
          
          if (controlPointsData && controlPointsData.type === 'FeatureCollection') {
            setControlPoints(controlPointsData);
          }
        } catch {
          // Control points are optional — silently ignore.
        }
      } catch {
        if (!isMounted) return;
        message.error(t("adjacent_crops.error.loadAudit", "Error al cargar la auditoría"));
        go({ to: "/audits" });
      } finally {
        if (isMounted) {
          setIsLoadingData(false);
        }
      }
    };

    loadAuditData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId]);

  // Handle photo upload
  const handlePhotoChange = (info: any) => {
    const { fileList } = info;
    
    const newPhotos: PhotoItem[] = fileList.map((file: UploadFile) => {
      if (file.originFileObj) {
        return {
          uid: file.uid,
          file: file.originFileObj as File,
          description: "",
          preview: URL.createObjectURL(file.originFileObj as File),
        };
      }
      return null;
    }).filter(Boolean);

    setPhotos(newPhotos);
  };

  const handlePhotoDescriptionChange = (uid: string, description: string) => {
    setPhotos((prev) =>
      prev.map((photo) =>
        photo.uid === uid ? { ...photo, description } : photo
      )
    );
  };

  const handleRemovePhoto = (uid: string) => {
    setPhotos((prev) => prev.filter((photo) => photo.uid !== uid));
  };

  /**
   * Convert file to base64
   */
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Handle form submission
  const handleSubmit = async (values: AdjacentCropFromAuditFormValues) => {
    if (!auditId) {
      message.error(t("adjacent_crops.error.noAuditId", "No se especificó una auditoría"));
      return;
    }

    if (!geometry) {
      message.error(t("adjacent_crops.error.noGeometry", "Por favor, marque la ubicación del cultivo en el mapa"));
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert photos to base64
      const photosBase64 = await Promise.all(
        photos.map(async (photo) => ({
          image_base64: await fileToBase64(photo.file),
          description: photo.description,
        }))
      );

      // Prepare data
      const data: any = {
        ...values,
        audit_id: parseInt(auditId),
        geom: geometry,
        photos: photosBase64,
      };

      // Create adjacent crop
      await adjacentCropService.createFromAudit(data);

      message.success(t("adjacent_crops.create.success", "Cultivo adyacente creado exitosamente"));

      // Clean up preview URLs
      photos.forEach((photo) => {
        URL.revokeObjectURL(photo.preview);
      });

      // Redirect back to audit
      go({ to: `/audits/${auditId}` });
    } catch (error: any) {
      message.error(
        error.message || t("adjacent_crops.create.error", "Error al crear el cultivo adyacente")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Create
      isLoading={isSubmitting}
      title={t("adjacent_crops.create.title", "Crear Cultivo Adyacente")}
      breadcrumb={
        <Breadcrumb
          items={[
            {
              title: <Link to="/"><HomeOutlined /> {t("home.title", "Inicio")}</Link>,
            },
            {
              title: <Link to="/audits"><AuditOutlined /> {t("audit.title", "Auditorías")}</Link>,
            },
            {
              title: <Link to={`/audits/${auditId}`}>{auditReference || auditId}</Link>,
            },
            {
              title: t("adjacent_crops.create.title", "Crear Cultivo Adyacente"),
            },
          ]}
        />
      }
      headerButtons={() => (
        <Button onClick={() => go({ to: `/audits/${auditId}` })}>
          {t("common.back", "Volver")}
        </Button>
      )}
      footerButtons={() => (
        <>
          <Button onClick={() => go({ to: `/audits/${auditId}` })}>
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={isSubmitting}
            icon={<EnvironmentOutlined />}
          >
            {t("common.save", "Guardar")}
          </Button>
        </>
      )}
    >
      <Form {...formProps} layout="vertical" onFinish={handleSubmit}>
        {/* Two-column layout: form on the left, sticky map on the
            right. On screens narrower than lg the columns stack so
            the map ends up below the form. */}
        <Row gutter={24}>
          <Col xs={24} lg={15}>
            {/* Basic Information Card */}
            <Card
              title={t("adjacent_crops.create.basicInfo", "Información Básica")}
              style={{ marginBottom: 24 }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("adjacent_crops.field.variety", "Variedad")}
                    name="variety"
                    rules={[
                      {
                        required: true,
                        message: t(
                          "adjacent_crops.validation.varietyRequired",
                          "La variedad es obligatoria",
                        ),
                      },
                    ]}
                    extra={t(
                      "adjacent_crops.variety_picker.extra_help",
                      "Busca por nombre. Si no existe, la creas al vuelo eligiendo su especie.",
                    )}
                  >
                    <AdjacentCropVarietyPicker
                      speciesOptions={
                        (speciesSelectProps.options as any) || []
                      }
                      disabled={isLoadingData}
                      onChange={(_id, variety) => {
                        // Sincroniza `species` en el form desde la
                        // variedad elegida — el usuario ya no lo elige
                        // aparte. Si limpia la variedad, limpiamos la
                        // especie también.
                        form.setFieldsValue({
                          species: variety?.species_id ?? undefined,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("adjacent_crops.field.species", "Especie")}
                    name="species"
                    rules={[
                      {
                        required: true,
                        message: t(
                          "adjacent_crops.validation.speciesRequired",
                          "La especie es obligatoria",
                        ),
                      },
                    ]}
                    extra={t(
                      "adjacent_crops.variety_picker.species_auto",
                      "Se rellena automáticamente al elegir la variedad. Puedes cambiarla si es necesario.",
                    )}
                  >
                    <Select
                      {...speciesSelectProps}
                      placeholder={t(
                        "adjacent_crops.placeholder.species",
                        "Seleccione una especie",
                      )}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toString()
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      allowClear
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("adjacent_crops.field.estimatedSurface", "Superficie Estimada (ha)")}
                    name="estimated_surface"
                    initialValue={0}
                    extra={
                      geometryAreaHa != null ? (
                        <span style={{ fontSize: 12 }}>
                          {t(
                            "adjacent_crops.polygon_area_hint",
                            "Superficie aproximada del polígono: {{ha}} ha",
                            { ha: geometryAreaHa.toFixed(2) },
                          )}
                          {" · "}
                          <a
                            onClick={(e) => {
                              e.preventDefault();
                              form.setFieldValue(
                                "estimated_surface",
                                Number(geometryAreaHa.toFixed(2)),
                              );
                            }}
                          >
                            {t(
                              "adjacent_crops.polygon_area_apply",
                              "Usar este valor",
                            )}
                          </a>
                        </span>
                      ) : null
                    }
                  >
                    <InputNumber
                      min={0}
                      step={0.1}
                      precision={2}
                      style={{ width: "100%" }}
                      placeholder="0.00"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("adjacent_crops.field.isProducerOwner", "¿Es propiedad del productor?")}
                    name="is_producer_owner"
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Switch />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label={t("adjacent_crops.field.comment", "Comentario")}
                    name="comment"
                    // Optional — the auditor can save without a
                    // comment when the polygon + species/variety are
                    // self-explanatory.
                  >
                    <TextArea
                      rows={4}
                      placeholder={t(
                        "adjacent_crops.placeholder.comment",
                        "Describa el cultivo adyacente detectado (opcional)...",
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Photos Card */}
            <Card
              title={t("adjacent_crops.create.photos", "Fotos")}
              style={{ marginBottom: 24 }}
            >
              <Dragger
                multiple
                accept="image/*"
                beforeUpload={() => false}
                onChange={handlePhotoChange}
                fileList={photos.map((p) => ({
                  uid: p.uid,
                  name: p.file.name,
                  status: "done" as const,
                }))}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {t("adjacent_crops.photos.dragDrop", "Haga clic o arrastre fotos aquí")}
                </p>
              </Dragger>

              {photos.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  {photos.map((photo) => (
                    <Card
                      key={photo.uid}
                      size="small"
                      style={{ marginBottom: 8 }}
                      extra={
                        <Button
                          type="link"
                          danger
                          size="small"
                          onClick={() => handleRemovePhoto(photo.uid)}
                        >
                          {t("common.remove", "Eliminar")}
                        </Button>
                      }
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <img
                            src={photo.preview}
                            alt="Preview"
                            style={{ width: "100%", height: "auto" }}
                          />
                        </Col>
                        <Col span={16}>
                          <Input
                            placeholder={t("adjacent_crops.photos.description", "Descripción de la foto")}
                            value={photo.description}
                            onChange={(e) =>
                              handlePhotoDescriptionChange(photo.uid, e.target.value)
                            }
                          />
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={9}>
            <div style={{ position: "sticky", top: 24 }}>
              <Card
                title={
                  <Space>
                    <EnvironmentOutlined />
                    {t("adjacent_crops.create.location", "Ubicación en el Mapa")}
                  </Space>
                }
                style={{ marginBottom: 24 }}
                styles={{ body: { padding: 12 } }}
              >
                <p
                  style={{
                    marginBottom: 12,
                    color: "#666",
                    fontSize: 12,
                  }}
                >
                  {t(
                    "adjacent_crops.create.locationNoteShort",
                    "Marca un punto o dibuja un polígono. Las parcelas auditadas aparecen en azul.",
                  )}
                </p>
                <div
                  style={{
                    height: 460,
                    width: "100%",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  {isLoadingData ? (
                    <div style={{ padding: "40px", textAlign: "center" }}>
                      <p>Cargando datos del mapa...</p>
                    </div>
                  ) : (
                    <AdjacentCropMapEditor
                      geometry={geometry}
                      onGeometryChange={setGeometry}
                      referencePlots={referencePlots}
                      controlPoints={controlPoints}
                      height="100%"
                    />
                  )}
                </div>
                {geometry && (
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 12,
                      color: "#722ed1",
                    }}
                  >
                    ✓{" "}
                    {geometry.type === "Point"
                      ? t("adjacent_crops.pointMarked", "Punto marcado")
                      : t("adjacent_crops.polygonDrawn", "Polígono dibujado")}
                  </div>
                )}
              </Card>
            </div>
          </Col>
        </Row>
      </Form>
    </Create>
  );
};


